import { getRepository } from '@things-factory/shell'
import type ResolverContext from '@things-factory/auth-base'
import { DataSummary } from '@things-factory/dataset'
import { finalizeLatestDataCollection } from '@things-factory/dataset/dist-server/controllers/finalize-data-collection'
import { KpiMetric } from './kpi-metric'
import { KpiValue, KpiValueInputType } from '../kpi-value/kpi-value'
import { Kpi } from '../kpi/kpi'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'
import { KpiFormulaService } from '../kpi/kpi-formula.service'

/**
 * KPI-Metric 집계/자동화 서비스 함수 (formula 지원, KPI Value 저장)
 * @param metricId KPI-Metric ID
 * @param domainId 도메인 ID
 * @param context ResolverContext (state에 domain, user, tx 등 포함)
 * @returns 저장된 KPI Value 배열
 */
export async function aggregateKpiMetricValue(metricId: string, domainId: string, context: ResolverContext) {
  const tx = context.state?.tx || getRepository(KpiMetric).manager

  // 1. metric 정보 조회
  const metric = await getRepository(KpiMetric).findOne({
    where: { id: metricId, domain: { id: domainId } }
  })
  if (!metric) throw new Error('Metric 정보 없음')
  if (!metric.active) throw new Error('비활성화된 Metric')

  // formula 분기 제거: metric은 항상 dataset 집계만 수행
  let values: any[] = []
  if (!metric.dataSetId || !metric.fieldName) throw new Error('Metric 매핑 정보 누락')
  await finalizeLatestDataCollection(metric.dataSetId, context)
  const summaries = await getRepository(DataSummary).find({
    where: { dataSet: { id: metric.dataSetId }, domain: { id: domainId } },
    order: { date: 'DESC', period: 'DESC' }
  })
  values = summaries
    .map(summary => {
      if (summary.summary && metric.fieldName in summary.summary) {
        return {
          date: summary.date,
          period: summary.period,
          value: summary.summary[metric.fieldName],
          org: {
            key01: summary.key01,
            key02: summary.key02,
            key03: summary.key03,
            key04: summary.key04,
            key05: summary.key05
          }
        }
      }
      return null
    })
    .filter(Boolean)

  // 5. KPI Value로 저장 (metric 단위, KPI 단위 formula는 후속)
  // metric이 속한 KPI 정보 조회 (여기서는 metric.name == kpi.name인 KPI를 임시로 매핑, 실제 연동 구조에 맞게 보완 필요)
  const kpi = await getRepository(Kpi).findOne({ where: { name: metric.name, domain: { id: domainId } } })
  if (!kpi) throw new Error('KPI 정보 없음 (metric.name과 동일한 KPI name 기준, 실제 연동 구조에 맞게 보완 필요)')

  // KPI Value version, valueDate, org 등 매핑
  const savedValues = []
  const kpiOrgScopeRepo = getRepository(KpiOrgScope, context.state?.tx)
  const kpiValueRepo = getRepository(KpiValue, context.state?.tx)

  for (const v of values) {
    const valueDate = v.date
    const orgData = v.org
    const version = kpi.version || 1
    const value = v.value
    if (value == null || isNaN(value)) continue

    // KpiOrgScope 찾기 또는 생성
    let kpiOrgScope: KpiOrgScope | null = null

    // key들로 조합된 org 식별자 생성
    const orgIdentifier =
      [orgData.key01, orgData.key02, orgData.key03, orgData.key04, orgData.key05].filter(Boolean).join('-') ||
      'unknown-org'

    // 기존 KpiOrgScope 조회 (entityName 기준)
    kpiOrgScope = await kpiOrgScopeRepo.findOne({
      where: { entityName: orgIdentifier, domain: { id: domainId } }
    })

    // 없으면 새로 생성
    if (!kpiOrgScope) {
      kpiOrgScope = await kpiOrgScopeRepo.save({
        entityType: 'DataSummary', // DataSummary에서 온 데이터임을 표시
        entityId: `datasummary-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        entityName: orgIdentifier,
        org: orgIdentifier, // legacy field
        scope01: orgData.key01,
        scope02: orgData.key02,
        scope03: orgData.key03,
        scope04: orgData.key04,
        scope05: orgData.key05,
        domain: kpi.domain,
        creator: context.state?.user,
        updater: context.state?.user
      })
    }

    // upsert KpiValue (동일 KPI, valueDate, kpiOrgScope, version 기준)
    const existing = await kpiValueRepo.findOne({
      where: {
        kpi: { id: kpi.id },
        valueDate,
        kpiOrgScope: { id: kpiOrgScope.id },
        version,
        domain: { id: domainId }
      }
    })

    let entity = existing || kpiValueRepo.create()
    entity.kpi = kpi
    entity.kpiId = kpi.id
    entity.version = version
    entity.valueDate = valueDate
    entity.value = value
    entity.kpiOrgScope = kpiOrgScope
    entity.kpiOrgScopeId = kpiOrgScope.id
    entity.inputType = KpiValueInputType.AUTO
    entity.source = 'AUTO-AGGREGATE'
    entity.domain = kpi.domain
    entity.creator = context.state?.user
    entity.updater = context.state?.user
    entity = await kpiValueRepo.save(entity)
    savedValues.push(entity)
  }
  return savedValues
}
